import { toDateFormat } from '../util';

// Runtime-only cache: routes and modal instances share it; reloads start fresh.
const entries = new Map();
const listeners = new Map();
const completions = new Set();
let completionChannel = null;
const cacheKey = (identity, gameSlug) => JSON.stringify([identity, gameSlug]);
const today = () => toDateFormat(new Date());

function ensureCompletionChannel() {
  if (completionChannel || typeof BroadcastChannel === 'undefined') return;
  try {
    completionChannel = new BroadcastChannel('game-stats-completion');
    completionChannel.onmessage = (event) => {
      const data = event?.data;
      if (typeof data?.gameSlug !== 'string' || typeof data?.completionKey !== 'string') return;
      invalidatePlayerGameStats(data.gameSlug, data.completionKey, false);
    };
  } catch (_) {
    // Cross-tab sync is optional; browser restrictions must not interrupt gameplay.
    completionChannel = null;
  }
}

export function getPlayerGameStatsSnapshot(identity, gameSlug) {
  const entry = entries.get(cacheKey(identity, gameSlug));
  return entry?.day === today() ? entry.response || null : null;
}

export function getCachedPlayerGameStats(identity, gameSlug, fetcher) {
  ensureCompletionChannel();
  const key = cacheKey(identity, gameSlug);
  const day = today();
  const cached = entries.get(key);
  if (cached?.day === day) {
    if (cached.response) return Promise.resolve(cached.response);
    if (cached.promise) return cached.promise;
  }
  const entry = { identity, gameSlug, day, response: null, promise: null };
  entries.set(key, entry);
  entry.promise = Promise.resolve().then(fetcher).then((response) => {
    // An older response cannot refill a cache invalidated by completion or midnight.
    if (entries.get(key) !== entry || day !== today()) {
      return getCachedPlayerGameStats(identity, gameSlug, fetcher);
    }
    if (!response?.success || !response.stats) throw new Error('Missing statistics');
    entry.response = response;
    entry.promise = null;
    return response;
  }).catch((error) => {
    if (entries.get(key) === entry) entries.delete(key);
    throw error;
  });
  return entry.promise;
}

export function subscribePlayerGameStats(gameSlug, listener) {
  const subscribers = listeners.get(gameSlug) || new Set();
  subscribers.add(listener);
  listeners.set(gameSlug, subscribers);
  return () => {
    subscribers.delete(listener);
    if (!subscribers.size) listeners.delete(gameSlug);
  };
}

export function invalidatePlayerGameStats(gameSlug, completionKey = null, broadcast = true) {
  if (completionKey) {
    const key = JSON.stringify([gameSlug, completionKey]);
    if (completions.has(key)) return;
    completions.add(key);
  }
  // Invalidate both anonymous and account scopes: linked histories may overlap.
  for (const [key, entry] of entries) {
    if (entry.gameSlug === gameSlug) entries.delete(key);
  }
  for (const listener of listeners.get(gameSlug) || []) listener();
  if (broadcast && completionKey) {
    ensureCompletionChannel();
    try {
      completionChannel?.postMessage({ gameSlug, completionKey });
    } catch (_) {
      // The local cache is already invalidated even if broadcasting fails.
    }
  }
}

export function clearPlayerGameStatsCache() {
  entries.clear();
  completions.clear();
  completionChannel?.close();
  completionChannel = null;
}
